/**
 * Award-derived minimum-engagement floors.
 *
 * cl 10.5 / HPSS cl 11.2 / Nurses cl 11.3 are payment minimums. Today they
 * only fire when a tenant has seeded `pay_ordinary_hours_profiles`. A SCHADS
 * tenant with no profile pays raw hours. This module is the award floor;
 * a profile may raise it, never lower it.
 *
 * Artifact:
 * docs/operations/clause-verifications/2026-08-15-aged-care-legislation-award-minimum-engagement.md
 */

export const AWARD_MINIMUM_ENGAGEMENT_VERSION = '1.0.0';
export const AWARD_MINIMUM_ENGAGEMENT_COMPONENT_CODE = 'AWARD_MINIMUM_ENGAGEMENT_TOP_UP';

export type AwardMinimumEngagementFloor = {
  hours: number;
  minutes: number;
  clauseRef: string;
  componentCode: string;
};

const SACS_STREAMS = new Set(['sacs', 'crisis_accommodation', 'social_community']);
const HOME_CARE_OR_DISABILITY_STREAMS = new Set([
  'home_care',
  'home_care_aged',
  'home_care_disability',
  'disability',
  'community',
  'family_day_care',
]);

function normalisedEmployment(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

function normalisedAward(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

function normalisedStream(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function isPartTimeOrCasual(employment: string): boolean {
  return employment === 'PART_TIME' || employment === 'CASUAL';
}

export function deriveAwardMinimumEngagement(input: {
  awardCode: string | null | undefined;
  employmentType: string | null | undefined;
  classificationStream?: string | null;
  isRemoteWork?: boolean;
}): AwardMinimumEngagementFloor | null {
  if (input.isRemoteWork === true) return null;
  const award = normalisedAward(input.awardCode);
  const employment = normalisedEmployment(input.employmentType);
  if (!award || !employment || employment === 'CONTRACTOR') return null;
  if (employment === 'FULL_TIME') return null;

  if (award === 'MA000100' && isPartTimeOrCasual(employment)) {
    const stream = normalisedStream(input.classificationStream);
    if (stream && SACS_STREAMS.has(stream)) {
      return {
        hours: 3,
        minutes: 180,
        clauseRef: 'MA000100 cl 10.5(a)',
        componentCode: AWARD_MINIMUM_ENGAGEMENT_COMPONENT_CODE,
      };
    }
    // Home care / disability / community / unknown stream → cl 10.5(b) 2h.
    // Unknown stream uses (b) to match SCHADS_MINIMUM_SHIFT_HOURS; SACS 3h
    // requires an explicit sacs-family stream.
    if (!stream || HOME_CARE_OR_DISABILITY_STREAMS.has(stream)) {
      return {
        hours: 2,
        minutes: 120,
        clauseRef: 'MA000100 cl 10.5(b)',
        componentCode: AWARD_MINIMUM_ENGAGEMENT_COMPONENT_CODE,
      };
    }
    return {
      hours: 2,
      minutes: 120,
      clauseRef: 'MA000100 cl 10.5(b)',
      componentCode: AWARD_MINIMUM_ENGAGEMENT_COMPONENT_CODE,
    };
  }

  if (award === 'MA000027' && employment === 'CASUAL') {
    return {
      hours: 3,
      minutes: 180,
      clauseRef: 'MA000027 cl 11.2',
      componentCode: AWARD_MINIMUM_ENGAGEMENT_COMPONENT_CODE,
    };
  }

  if (award === 'MA000034' && employment === 'CASUAL') {
    return {
      hours: 2,
      minutes: 120,
      clauseRef: 'MA000034 cl 11.3',
      componentCode: AWARD_MINIMUM_ENGAGEMENT_COMPONENT_CODE,
    };
  }

  return null;
}

export function resolveAwardMinimumEngagementFloor(input: {
  awardCode: string | null | undefined;
  employmentType: string | null | undefined;
  classificationStream?: string | null;
  isRemoteWork?: boolean;
  profileMinutes?: number | null;
  profileClauseRef?: string | null;
  profileComponentCode?: string | null;
  hpssMinimumEngagementHours?: number | null;
}): {
  hours: number;
  clauseRef: string;
  componentCode: string;
  source: 'AWARD' | 'PROFILE' | 'NONE';
} {
  const derived = deriveAwardMinimumEngagement(input);
  const profileHours =
    typeof input.profileMinutes === 'number' &&
    Number.isFinite(input.profileMinutes) &&
    input.profileMinutes > 0
      ? input.profileMinutes / 60
      : 0;
  const hpssHours =
    normalisedAward(input.awardCode) === 'MA000027' &&
    typeof input.hpssMinimumEngagementHours === 'number' &&
    Number.isFinite(input.hpssMinimumEngagementHours) &&
    input.hpssMinimumEngagementHours > 0
      ? input.hpssMinimumEngagementHours
      : 0;

  const awardHours = derived?.hours ?? 0;
  const hours = Math.max(awardHours, profileHours, hpssHours);
  if (hours <= 0) {
    return { hours: 0, clauseRef: '', componentCode: '', source: 'NONE' };
  }

  if (profileHours + 1e-9 >= hours && profileHours > 0) {
    return {
      hours,
      clauseRef:
        input.profileClauseRef?.trim() ||
        derived?.clauseRef ||
        'Ordinary-hours profile minimum engagement',
      componentCode:
        input.profileComponentCode?.trim() ||
        derived?.componentCode ||
        AWARD_MINIMUM_ENGAGEMENT_COMPONENT_CODE,
      source: 'PROFILE',
    };
  }

  if (hpssHours + 1e-9 >= hours && hpssHours > 0 && awardHours <= hpssHours && profileHours <= 0) {
    return {
      hours,
      clauseRef: derived?.clauseRef ?? 'MA000027 cl 11.2 / 11.3',
      componentCode: derived?.componentCode ?? 'HPSS_MINIMUM_ENGAGEMENT_TOP_UP',
      source: 'AWARD',
    };
  }

  return {
    hours,
    clauseRef: derived?.clauseRef ?? input.profileClauseRef?.trim() ?? '',
    componentCode:
      derived?.componentCode ??
      input.profileComponentCode?.trim() ??
      AWARD_MINIMUM_ENGAGEMENT_COMPONENT_CODE,
    source: 'AWARD',
  };
}

export function isPartTimeOrCasualEmployment(employmentType: string | null | undefined): boolean {
  return isPartTimeOrCasual(normalisedEmployment(employmentType));
}
